/**
 * Multi-layer config resolution with observability.
 *
 * Resolves the backend URL from one of three tiers, in priority order:
 *
 *   Tier 1 — RUNTIME (preferred)
 *     `window.RUNTIME_CONFIG.API_URL` populated by /config.js, which is
 *     regenerated by every successful deploy with the latest values from
 *     GitHub Secrets. Allows fixing a wrong URL by editing one file on
 *     EC2 — no rebuild required.
 *
 *   Tier 2 — BUILD-TIME (CI-baked, fallback)
 *     `process.env.REACT_APP_BACKEND_URL` baked into the bundle at build
 *     time. Used if /config.js is missing or empty (e.g. a deploy that
 *     forgot to regenerate it). Source labelled `build-time-fallback`
 *     and surfaced via the admin banner.
 *
 *   Tier 3 — SMART INFERENCE (last-resort)
 *     `window.location.origin`. Works because nginx proxies /api/* on
 *     the same domain → relative paths resolve correctly. Means the site
 *     keeps functioning even if BOTH config layers above are broken.
 *     Source labelled `inferred-fallback` (red admin alert).
 *
 * The active tier is exposed via getConfigSource() so the admin dashboard
 * can show a visible warning when we're running on a fallback. The
 * frontend also POSTs its detected source to /api/health/config-report
 * on load so the backend keeps server-side telemetry of which tier is
 * active across the user base.
 */

let _cached = null;

const isEmpty = (v) => !v || v === 'undefined' || v === 'null' || (typeof v === 'string' && v.trim() === '');

const trimTrailingSlash = (u) => (typeof u === 'string' ? u.replace(/\/$/, '') : u);

// If the configured URL is the same site as the page, differing only by a
// leading "www." (e.g. config says wineryfinderapp.com, page is on
// www.wineryfinderapp.com), use the page's own origin instead. Otherwise a
// CDN apex→www redirect turns every API call into a cross-origin request
// that the browser blocks (redirects are not allowed on CORS preflights).
const preferSameOrigin = (u, w) => {
  if (!w?.location?.origin) return u;
  try {
    const bare = (h) => h.replace(/^www\./, '');
    const target = new URL(u);
    if (target.host !== w.location.host && bare(target.host) === bare(w.location.host)) {
      return w.location.origin;
    }
  } catch {
    // not an absolute URL — leave as-is
  }
  return u;
};

const resolveConfig = () => {
  if (_cached) return _cached;

  const w = typeof window !== 'undefined' ? window : null;
  const runtimeUrl = w?.RUNTIME_CONFIG?.API_URL;
  const buildTimeUrl = process.env.REACT_APP_BACKEND_URL;

  let url;
  let source;

  if (!isEmpty(runtimeUrl)) {
    url = preferSameOrigin(trimTrailingSlash(runtimeUrl), w);
    source = w?.RUNTIME_CONFIG?.CONFIG_SOURCE || 'production';
  } else if (!isEmpty(buildTimeUrl)) {
    url = preferSameOrigin(trimTrailingSlash(buildTimeUrl), w);
    source = 'build-time-fallback';
  } else if (w?.location?.origin) {
    url = trimTrailingSlash(w.location.origin);
    source = 'inferred-fallback';
  } else {
    url = '';
    source = 'unknown';
  }

  _cached = {
    url,
    source,
    deployedAt: w?.RUNTIME_CONFIG?.DEPLOYED_AT || null,
    commitSha: w?.RUNTIME_CONFIG?.COMMIT_SHA || null,
  };

  if (source !== 'production' && typeof console !== 'undefined') {
    console.warn(
      `[runtimeConfig] Backend URL resolved via FALLBACK (${source}): ${url || '(empty)'}. ` +
      `Primary /config.js is missing or empty — check deploy pipeline.`
    );
  }

  return _cached;
};

export const getBackendUrl = () => resolveConfig().url;
export const getConfigSource = () => resolveConfig().source;
export const getConfigDiagnostics = () => ({ ...resolveConfig() });
export const isFallbackActive = () => resolveConfig().source !== 'production';

/**
 * Fire-and-forget telemetry POST so the backend knows which config tier
 * is active in the field. Called once on app boot from src/index.js.
 * Failure is silent — telemetry is non-critical.
 */
export const reportConfigToBackend = async () => {
  if (typeof window === 'undefined') return;
  const diag = resolveConfig();
  try {
    await fetch(`${diag.url}/api/health/config-report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source: diag.source,
        url: diag.url,
        deployed_at: diag.deployedAt,
        commit_sha: diag.commitSha,
        user_agent: window.navigator?.userAgent?.slice(0, 200) || null,
        path: window.location?.pathname || null,
      }),
      keepalive: true,
    });
  } catch {
    // non-critical
  }
};
